// What the training pages show, worked out from the stored records.
// Pure functions, no Firebase, so the tests cover them.
// Words: TRAINING_PORTAL_WORDS.md and TRAINING_RECORD_WORDS.md (approved 2026-10-03).

import type { StationProgress } from "./field-demo";
import {
  NODES,
  QUIZZES,
  isCurrentOn,
  requiredCorrect,
  type NodeId,
  type Quiz,
  type QuizId,
  type QuizQuestion,
  type TrainingRecord,
} from "./training";

/**
 * The first five sites, as "City, ST". Only the fallback now: names come from the sites collection through
 * setLiveSiteNames once it is read, so a site made by Add Site shows its own name (SITE_GROWTH_FIXES_WORDS.md item 3).
 */
export const SITE_NAMES: Record<string, string> = {
  "00": "Nicholasville, KY",
  "01": "Springfield, KY",
  "02": "Flemingsburg, KY",
  "04": "Shipshewana, IN",
  "10": "Baconton, GA",
};

export const MODULE_NAMES: Record<string, string> = {
  "TRN-001": "Electrical Hazards, Risks & Controls",
  "TRN-002": "Alltech's Electrical Safety Program",
  "TRN-003": "Electric Shock and Arc Flash",
  "TRN-004": "PPE Selection, Inspection, and Use",
  "TRN-005": "Meter Safety (CAT II-IV) and Electrical Tools",
  "TRN-006": "Lockout / Tagout Review with LDL Verification",
};

export const QUESTION_TARGET = 15;

export const EARNED_BY_LABEL: Record<TrainingRecord["earnedBy"], string> = {
  quizPass: "Quiz pass",
  correctedInClass: "Corrected in class",
  manualClose: "Closed by hand",
  carriedOver: "Carried over",
  fieldDemo: "Field demonstration",
};

// ---- Quiz Bank ----

export interface QuizSummary {
  quiz: Quiz;
  modules: string[];
  validDays: number | null;
  questions: QuizQuestion[];
  activeCount: number;
  passLine: string;
}

/** One summary per quiz, questions in quiz order. Uses the stored quiz doc when there is one. */
export function summarizeQuizzes(stored: Quiz[], questions: QuizQuestion[]): QuizSummary[] {
  return QUIZZES.map((fallback) => {
    const quiz = stored.find((q) => q.quizId === fallback.quizId) ?? fallback;
    const node = NODES.find((n) => n.nodeId === quiz.nodeId) ?? NODES[0]!;
    const qs = questions
      .filter((q) => q.quizId === quiz.quizId)
      .sort((a, b) => a.questionNumber - b.questionNumber);
    const activeCount = qs.filter((q) => q.isActive).length;
    return {
      quiz,
      modules: node.modules,
      validDays: node.validDays,
      questions: qs,
      activeCount,
      passLine: passLine(quiz.passMarkPct, activeCount),
    };
  });
}

/** "80 percent · 12 of 15 correct to pass". */
export function passLine(passMarkPct: number, activeCount: number): string {
  if (activeCount <= 0) return `${passMarkPct} percent`;
  return `${passMarkPct} percent · ${requiredCorrect(activeCount, passMarkPct)} of ${activeCount} correct to pass`;
}

export function quizIdFrom(value: string | undefined): QuizId {
  return value === "quiz2" || value === "quiz3" ? value : "quiz1";
}

// ---- Training Portal ----

export type NodeState = "done" | "open" | "locked";

/** What the portal needs from a quiz attempt. The attempt holds the score (TRAINING_RECORD_WORDS.md). */
export interface AttemptScore {
  attemptId: string;
  qewId: string;
  quizId: QuizId;
  /** The class it was taken in. Class Results count by session. */
  sessionId: string;
  attemptNumber: number;
  scorePct: number;
  questionsCorrect: number;
  questionsTotal: number;
  isPassed: boolean;
  correctedScorePct: number | null;
  takenAt: string;
}

export interface NodeRow {
  nodeId: NodeId;
  nodeName: string;
  modules: string[];
  quizId: QuizId | null;
  validDays: number | null;
  state: NodeState;
  /** The current record. Null when the node was never done or its record expired. */
  record: TrainingRecord | null;
  /** The latest record when it has expired. The node is open again (same rule as the quiz and the server). */
  expired: TrainingRecord | null;
  /** The attempt that closed the node, on a quiz pass or a correction. */
  attempt: AttemptScore | null;
  /** On an open node: the latest failed attempt not yet corrected. */
  failed: AttemptScore | null;
}

export interface Trainee {
  qewId: string;
  name: string;
  siteNumber: string;
  nodes: NodeRow[];
  /** Nodes current today. */
  doneCount: number;
  /** All four nodes done at some point. Training complete; a later expiry never takes it away. */
  isQew: boolean;
  /** Nodes whose latest record has expired. */
  expiredCount: number;
  allCarriedOver: boolean;
  /** The open node has a failed attempt not yet corrected. */
  failed: AttemptScore | null;
  /** Node 4 demo stations done so far (people/{qewId}.fieldDemoStations). */
  stations: StationProgress;
  /** His LOTO date for the trainee page LOTO row (Stage 4b). */
  loto: LotoRow;
}

/** The LOTO row on the Training Portal trainee page. */
export interface LotoRow {
  /** The date he reported. Null when none is on file. */
  reportedOn: string | null;
  verifiedOn: string | null;
  verifiedByName: string | null;
}

export type LotoRowState = "verified" | "waiting" | "none";

export function lotoRowState(l: LotoRow): LotoRowState {
  return l.verifiedOn ? "verified" : l.reportedOn ? "waiting" : "none";
}

/**
 * Nodes open in order. The first one not current is open, the rest are locked.
 * An expired node is open again, same as the quiz page and the server (currentNodes).
 * Today is YYYY-MM-DD in site time. Left blank, nothing counts as expired.
 */
export function nodeRows(records: TrainingRecord[], attempts: readonly AttemptScore[] = [], today = ""): NodeRow[] {
  let openGiven = false;
  return NODES.map((node) => {
    const latest =
      records
        .filter((r) => r.nodeId === node.nodeId)
        .sort((a, b) => b.completedOn.localeCompare(a.completedOn))[0] ?? null;
    const current = latest && isCurrentOn(latest.expiresOn, today) ? latest : null;
    const record = current;
    const expired = latest && !current ? latest : null;
    let state: NodeState;
    if (record) state = "done";
    else if (!openGiven) {
      state = "open";
      openGiven = true;
    } else state = "locked";
    const attempt = record?.quizAttemptId ? (attempts.find((a) => a.attemptId === record.quizAttemptId) ?? null) : null;
    const failed =
      state === "open" && node.quizId
        ? (attempts
            .filter((a) => a.quizId === node.quizId && !a.isPassed && a.correctedScorePct == null)
            // On a retake, only fails since the expired pass count.
            .filter((a) => !expired || a.takenAt.slice(0, 10) > expired.completedOn)
            .sort((a, b) => b.takenAt.localeCompare(a.takenAt))[0] ?? null)
        : null;
    return { ...node, modules: [...node.modules], state, record, expired, attempt, failed };
  });
}

/** The score line on a closed node. Carried over has none. */
export function scoreLine(record: TrainingRecord, attempt: AttemptScore | null): string {
  if (record.earnedBy === "correctedInClass" && attempt) {
    return `First try ${attempt.scorePct}% (${attempt.questionsCorrect} of ${attempt.questionsTotal}) · corrected to ${attempt.correctedScorePct ?? 100}%`;
  }
  if (record.earnedBy === "quizPass" && attempt) return `Passed ${attempt.scorePct}% (${attempt.questionsCorrect} of ${attempt.questionsTotal})`;
  if (record.scorePct != null) return `Score ${record.scorePct}%`;
  return "";
}

/**
 * Everyone at the site, by QEW ID: anyone with a training record, plus any
 * claimed QEW tag at the site, so a new trainee shows before Node 1 closes.
 */
export function rosterForSite(
  records: TrainingRecord[],
  siteNumber: string,
  qewTags: readonly { badgeId: string; name: string }[] = [],
  attempts: readonly AttemptScore[] = [],
  today = "",
  stations: Readonly<Record<string, StationProgress>> = {},
  loto: Readonly<Record<string, LotoRow>> = {},
): Trainee[] {
  const bySite = records.filter((r) => r.siteNumber === siteNumber);
  const tags = qewTags.filter((t) => t.badgeId.startsWith(`${siteNumber}-`));
  const ids = [...new Set([...bySite.map((r) => r.qewId), ...tags.map((t) => t.badgeId)])].sort();
  return ids.map((qewId) => {
    const mine = bySite.filter((r) => r.qewId === qewId);
    const nodes = nodeRows(
      mine,
      attempts.filter((a) => a.qewId === qewId),
      today,
    );
    const doneCount = nodes.filter((n) => n.state === "done").length;
    const everDone = new Set(mine.map((r) => r.nodeId));
    return {
      qewId,
      name: mine[0]?.name ?? tags.find((t) => t.badgeId === qewId)?.name ?? qewId,
      siteNumber,
      nodes,
      doneCount,
      isQew: NODES.every((n) => everDone.has(n.nodeId)),
      expiredCount: nodes.filter((n) => n.expired).length,
      allCarriedOver: mine.length > 0 && mine.every((r) => r.earnedBy === "carriedOver"),
      failed: nodes.find((n) => n.failed)?.failed ?? null,
      stations: stations[qewId] ?? {},
      loto: loto[qewId] ?? { reportedOn: null, verifiedOn: null, verifiedByName: null },
    };
  });
}

// ---- Class Results (projector safe: counts only, no names) ----

export interface QuizResult {
  quizId: QuizId;
  /** Trainees who took this quiz in the session. */
  took: number;
  /** Passed on an attempt, or corrected in class. */
  passed: number;
  /** Of the passed, how many got there by correction. */
  corrected: number;
  /** Took it, not passed, not corrected yet. */
  open: number;
}

export interface ClassResults {
  trainees: number;
  quizzes: QuizResult[];
}

/** One row per quiz for one session. Each trainee counts once per quiz, on their best result. */
export function classResults(attempts: readonly AttemptScore[], sessionId: string): ClassResults {
  const mine = attempts.filter((a) => a.sessionId === sessionId);
  const quizzes = QUIZZES.map((q) => {
    const byTrainee = new Map<string, AttemptScore[]>();
    for (const a of mine.filter((x) => x.quizId === q.quizId)) byTrainee.set(a.qewId, [...(byTrainee.get(a.qewId) ?? []), a]);
    let passed = 0;
    let corrected = 0;
    for (const list of byTrainee.values()) {
      if (list.some((a) => a.isPassed)) passed += 1;
      else if (list.some((a) => a.correctedScorePct != null)) {
        passed += 1;
        corrected += 1;
      }
    }
    return { quizId: q.quizId, took: byTrainee.size, passed, corrected, open: byTrainee.size - passed };
  });
  return { trainees: new Set(mine.map((a) => a.qewId)).size, quizzes };
}

/** Live site names from the sites collection, "Name, ST". Empty until read. */
let liveSiteNames: Record<string, string> = {};

/** Sets the live names from the sites collection. Name first, then state, the same shape as SITE_NAMES. */
export function setLiveSiteNames(sites: readonly { siteNumber: string; name: string; state?: string }[]): void {
  liveSiteNames = Object.fromEntries(sites.map((s) => [s.siteNumber, s.state ? `${s.name}, ${s.state}` : s.name]));
}

/** "Mankato, MN". The live name when read, else the fallback list, else "Site 03". */
export function siteNameOf(siteNumber: string): string {
  return liveSiteNames[siteNumber] ?? SITE_NAMES[siteNumber] ?? `Site ${siteNumber}`;
}

/** Sites that have anyone on them, plus the known sites, in number order. */
export function sitesWithCounts(records: TrainingRecord[]): { siteNumber: string; name: string; qewCount: number }[] {
  const known = Object.keys(liveSiteNames).length ? Object.keys(liveSiteNames) : Object.keys(SITE_NAMES);
  const numbers = new Set([...known, ...records.map((r) => r.siteNumber)]);
  return [...numbers].sort().map((siteNumber) => ({
    siteNumber,
    name: siteNameOf(siteNumber),
    qewCount: new Set(records.filter((r) => r.siteNumber === siteNumber).map((r) => r.qewId)).size,
  }));
}

/** 2027-09-17 reads 09/17/2027, same as the live portal. */
export function usDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${m}/${d}/${y}` : iso;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase())
    .join("")
    .slice(0, 2);
}
